// SPDX-License-Identifier: BUSL-1.1

/**
 * Sheet/drawer showing full job detail (trigger, lifecycle, payloads, logs).
 *
 * Extracted from `routes/agent-detail.tsx` during the P3b refactor.
 */

import { useContext, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { PiSessionPanel } from '@/components/pi-session-panel';
import {
  FleetActivityStreamContext,
  type FleetActivityStreamContextValue,
} from '@/components/live-activity-cell';
import { JobLiveActivitySection } from './job-live-activity-section';
import { ArchiveRunLinks } from './archive-links';
import type { PiSession } from '@/types/api';
import { showSuccess, showError } from '@/lib/toast';
import { formatDuration } from './helpers';
import type { Job } from './types';

/**
 * Copy text to the clipboard, with a toast.
 *
 * The pi session UUID is the join key between `ops_agent_jobs` and
 * `ops_agent_activity` (when activity rows carry sessionId — currently a
 * runner-side TODO). Until that backfill lands, operators reach for the
 * UUID to grep runner logs / `pi --resume`-replay locally. Cheap to copy,
 * high signal for cross-tool debugging.
 */
async function copyToClipboard(text: string, label: string): Promise<void> {
  try {
    if (typeof window !== 'undefined' && window.navigator?.clipboard) {
      await window.navigator.clipboard.writeText(text);
      showSuccess(`${label} copied`);
      return;
    }
    showError(`Clipboard not available`, `Copy manually: ${text}`);
  } catch (err) {
    showError(
      `Failed to copy ${label}`,
      err instanceof Error ? err.message : 'Unknown clipboard error',
    );
  }
}

export function JobDetailSheet({
  job,
  jobList,
  open,
  onClose,
  onSelectJob,
  onTaskCompleted,
}: {
  job: Job | null;
  jobList: Job[];
  open: boolean;
  onClose: () => void;
  onSelectJob: (job: Job) => void;
  /**
   * NocoDB #4790. Fired when a `task_completed` activity event arrives
   * for the currently-open job. Parent should invalidate its jobs query
   * so the modal re-renders with the final status + outputPayload.
   * Optional — modal still works without it but the operator will see
   * stale `processing` status until the parent's next refetch.
   */
  onTaskCompleted?: () => void;
}) {
  // NocoDB #4790 — subscribe to the org-wide fleet stream (provided by
  // <FleetActivityStreamProvider> on agent-detail.tsx). Hook MUST be
  // called unconditionally to keep React's hook order stable, so it runs
  // before the early `if (!job)` return below. The hook is a pure
  // useContext read with no side effects, so it's free to call when no
  // job is open.
  const fleetCtx = useContext<FleetActivityStreamContextValue | null>(
    FleetActivityStreamContext,
  );
  // Fire onTaskCompleted at most once per (job.id) lifecycle. Without
  // the ref guard, re-renders during the refetch window could trigger
  // multiple invalidations.
  const completedJobIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!job || !fleetCtx || !onTaskCompleted) return;
    if (completedJobIdRef.current === job.id) return;
    const hit = fleetCtx.events.find(
      (e) =>
        e.kind === 'task_completed' &&
        (e.payload as { task_id?: string } | undefined)?.task_id === job.id,
    );
    if (hit) {
      completedJobIdRef.current = job.id;
      onTaskCompleted();
    }
  }, [fleetCtx, job, onTaskCompleted]);

  if (!job) return null;

  const currentIndex = jobList.findIndex((j) => j.id === job.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < jobList.length - 1;

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-900/40 text-yellow-400',
    processing: 'bg-blue-900/40 text-blue-400',
    completed: 'bg-green-900/40 text-green-400',
    failed: 'bg-red-900/40 text-red-400',
    // MT-RUN-55: amber (not red) — see job-columns.tsx for full rationale.
    routing_failed: 'bg-amber-900/40 text-amber-400',
    skipped: 'bg-neutral-700 text-neutral-400',
    cancelled: 'bg-neutral-700 text-neutral-400',
  };

  // Extract logs from outputPayload if present.
  // OutputPayload has an `[key: string]: unknown` index signature, so we
  // narrow each field via a small helper before treating it as a string.
  const asString = (v: unknown): string =>
    typeof v === 'string' ? v : '';

  let logs = '';
  let cleanOutputPayload: Record<string, unknown> | null = null;
  const piSession: PiSession | undefined = job.outputPayload?.piSession;

  if (job.outputPayload && typeof job.outputPayload === 'object') {
    const payload = job.outputPayload;

    // Support multiple log field names: logs, executionLogs, stdout/stderr
    const stdout =
      asString(payload.stdout) ||
      asString(payload.logs) ||
      asString(payload.executionLogs);
    const stderr = asString(payload.stderr);

    if (stdout || stderr) {
      logs = [
        stdout ? `[OUTPUT]\n${stdout}` : '',
        stderr ? `[STDERR]\n${stderr}` : '',
      ]
        .filter(Boolean)
        .join('\n\n');
    }

    // Create a clean output payload without log fields to avoid duplication.
    // We also strip `piSession` since it has its own dedicated panel.
    const rest: Record<string, unknown> = { ...payload };
    delete rest.stdout;
    delete rest.stderr;
    delete rest.logs;
    delete rest.executionLogs;
    delete rest.piSession;

    if (Object.keys(rest).length > 0) {
      cleanOutputPayload = rest;
    }
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" showCloseButton={false}>
        <div className="flex items-center justify-between border-b border-neutral-800 pb-4 mt-2">
          <div>
            <h2 className="text-lg font-semibold text-neutral-100 flex items-center gap-3">
              Request
              <span
                className={`inline-flex rounded px-2 py-0.5 text-xs ${statusColors[job.status] ?? 'bg-neutral-700 text-neutral-400'}`}
                title={
                  job.status === 'routing_failed'
                    ? 'No runner advertises a capability for this agent_type — the job will not be reviewed until one does.'
                    : undefined
                }
              >
                {job.status.replace(/_/g, ' ')}
              </span>
            </h2>
            <p className="text-xs text-neutral-500 font-mono mt-1">{job.id}</p>
            {/* Console → Archive deep-link (ADR 009). The Archive URL is
                agent-scoped: G1 authorizes job.agentId before resolving the run,
                whose own id (job.id) is the Archive episode id (resolved against
                ops_agent_jobs.id). (NocoDB #4843) */}
            <div className="mt-1 -ml-2">
              <ArchiveRunLinks agentId={job.agentId} runId={job.id} />
            </div>
          </div>
          <div className="flex items-center gap-4 text-neutral-400">
            <div className="flex items-center gap-2 text-sm">
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={!hasPrev}
                onClick={() => hasPrev && onSelectJob(jobList[currentIndex - 1])}
              >
                &lt;
              </Button>
              <span className="min-w-[40px] text-center">{currentIndex >= 0 ? currentIndex + 1 : '-'} / {jobList.length}</span>
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={!hasNext}
                onClick={() => hasNext && onSelectJob(jobList[currentIndex + 1])}
              >
                &gt;
              </Button>
            </div>
            <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="mt-6 space-y-6">
          {/* Live activity stream — NocoDB #4790 */}
          <JobLiveActivitySection
            job={job}
            fleetCtx={fleetCtx}
          />

          {/* Trigger Info */}
          <section>
            <h3 className="mb-3 text-sm font-medium text-neutral-300">Trigger</h3>
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <dt className="text-neutral-500">Source</dt>
                <dd className="font-medium text-neutral-200">{job.trigger}</dd>
              </div>
              {job.triggerSource && (
                <div>
                  <dt className="text-neutral-500">Source Type</dt>
                  <dd className="font-medium text-neutral-200">{job.triggerSource}</dd>
                </div>
              )}
              {job.triggerEventId && (
                <div className="col-span-2">
                  <dt className="text-neutral-500">Event ID</dt>
                  <dd className="font-mono text-xs text-neutral-400">{job.triggerEventId}</dd>
                </div>
              )}
            </dl>
          </section>

          {/* Timing Pipeline (fal.ai style) */}
          <section>
            <h3 className="mb-3 text-sm font-medium text-neutral-300">Lifecycle & Timing</h3>

            <div className="mb-4 rounded-md border border-neutral-800 bg-neutral-900/50 p-4">
              <div className="flex items-center justify-between text-xs text-neutral-400">
                <div className="flex flex-col">
                  <span className="font-semibold text-neutral-300">Submitted</span>
                  <span>{new Date(job.createdAt).toLocaleTimeString()}</span>
                </div>

                {job.startedAt && (
                  <>
                    <div className="h-px flex-1 bg-neutral-800 mx-4 relative">
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-neutral-800 px-2 py-0.5 text-[10px]">
                        Queue: {formatDuration(Math.floor((new Date(job.startedAt).getTime() - new Date(job.createdAt).getTime()) / 1000))}
                      </div>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="font-semibold text-blue-400">Started</span>
                      <span>{new Date(job.startedAt).toLocaleTimeString()}</span>
                    </div>
                  </>
                )}

                {job.startedAt && job.completedAt && (
                  <>
                    <div className="h-px flex-1 bg-neutral-800 mx-4 relative">
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-neutral-800 px-2 py-0.5 text-[10px]">
                        Exec: {formatDuration(Math.floor((new Date(job.completedAt).getTime() - new Date(job.startedAt).getTime()) / 1000))}
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="font-semibold text-green-400">Finished</span>
                      <span>{new Date(job.completedAt).toLocaleTimeString()}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-neutral-500">Total Duration</dt>
                <dd className="font-medium text-neutral-200 mt-0.5">
                  {job.completedAt ? formatDuration(Math.floor((new Date(job.completedAt).getTime() - new Date(job.createdAt).getTime()) / 1000)) : '-'}
                </dd>
              </div>
              {job.cost != null && (
                <div>
                  <dt className="text-neutral-500">Cost</dt>
                  <dd className="font-medium text-neutral-200 mt-0.5">${Number(job.cost).toFixed(4)}</dd>
                </div>
              )}
              <div>
                <dt className="text-neutral-500">Runner ID</dt>
                <dd className="font-mono text-xs text-neutral-200 mt-0.5">{job.agentId.split('-')[0] ?? job.agentId}</dd>
              </div>
              {(job.retryCount ?? 0) > 0 && (
                <div>
                  <dt className="text-neutral-500">Retries</dt>
                  <dd className="font-medium text-neutral-200 mt-0.5">{job.retryCount}</dd>
                </div>
              )}
              {job.sessionId && (
                <div>
                  <dt className="text-neutral-500">Session link</dt>
                  <dd className="mt-0.5">
                    <button
                      type="button"
                      onClick={() => void copyToClipboard(job.sessionId!, 'Session ID')}
                      title={`Copy full ID: ${job.sessionId}`}
                      className="font-mono text-xs text-purple-400 hover:text-purple-300 hover:underline focus:outline-none focus:ring-1 focus:ring-purple-500 rounded px-1 -mx-1"
                      aria-label={`Copy session ID ${job.sessionId}`}
                    >
                      {job.sessionId.slice(0, 8)}…
                    </button>
                  </dd>
                </div>
              )}
              {job.piSessionId && (
                <div>
                  <dt className="text-neutral-500">pi session</dt>
                  <dd className="mt-0.5">
                    <button
                      type="button"
                      onClick={() => void copyToClipboard(job.piSessionId!, 'pi session UUID')}
                      title={`Copy full UUID: ${job.piSessionId}`}
                      className="font-mono text-xs text-cyan-400 hover:text-cyan-300 hover:underline focus:outline-none focus:ring-1 focus:ring-cyan-500 rounded px-1 -mx-1"
                      aria-label={`Copy pi session UUID ${job.piSessionId}`}
                    >
                      {job.piSessionId.slice(0, 8)}…
                    </button>
                  </dd>
                </div>
              )}
            </dl>
          </section>

          {/* Payloads & Logs */}
          <div className="space-y-4 pt-4 border-t border-neutral-800">
            <details className="group [&_summary::-webkit-details-marker]:hidden" open>
              <summary className="flex cursor-pointer items-center justify-between rounded-md bg-neutral-900 px-4 py-2 font-medium text-neutral-300 hover:bg-neutral-800">
                Input
                <span className="transition group-open:rotate-180">▼</span>
              </summary>
              <div className="mt-2 rounded-md bg-black p-4">
                {job.inputPayload != null && Object.keys(job.inputPayload).length > 0 ? (
                  <pre className="max-h-96 overflow-auto font-mono text-xs text-neutral-400">
                    {JSON.stringify(job.inputPayload as object, null, 2)}
                  </pre>
                ) : (
                  <span className="text-sm text-neutral-500">No input provided</span>
                )}
              </div>
            </details>

            <details className="group [&_summary::-webkit-details-marker]:hidden" open>
              <summary className="flex cursor-pointer items-center justify-between rounded-md bg-neutral-900 px-4 py-2 font-medium text-neutral-300 hover:bg-neutral-800">
                Output
                <span className="transition group-open:rotate-180">▼</span>
              </summary>
              <div className="mt-2 rounded-md bg-black p-4">
                {cleanOutputPayload != null ? (
                  <>
                    {typeof cleanOutputPayload.findings === 'string' && (
                      <div className="mb-4">
                        <h4 className="text-xs font-medium text-neutral-400 mb-2">Findings</h4>
                        <pre className="whitespace-pre-wrap text-xs bg-neutral-900 p-3 rounded overflow-auto max-h-96 text-neutral-300">
                          {cleanOutputPayload.findings}
                        </pre>
                      </div>
                    )}
                    {(cleanOutputPayload.cost !== undefined || cleanOutputPayload.duration !== undefined) && (
                      <div className="flex gap-6 mb-4 text-sm">
                        {cleanOutputPayload.cost !== undefined && (
                          <div>
                            <dt className="text-neutral-500 text-xs">Cost</dt>
                            <dd className="text-green-400 font-mono">${Number(cleanOutputPayload.cost).toFixed(4)}</dd>
                          </div>
                        )}
                        {cleanOutputPayload.duration !== undefined && (
                          <div>
                            <dt className="text-neutral-500 text-xs">Duration</dt>
                            <dd className="text-neutral-300 font-mono">{Math.round(Number(cleanOutputPayload.duration) / 1000)}s</dd>
                          </div>
                        )}
                      </div>
                    )}
                    {Object.keys(cleanOutputPayload).length > 0 && typeof cleanOutputPayload.findings !== 'string' && (
                      <pre className="max-h-96 overflow-auto font-mono text-xs text-neutral-400">
                        {JSON.stringify(cleanOutputPayload, null, 2)}
                      </pre>
                    )}
                  </>
                ) : (
                  <span className="text-sm text-neutral-500">No output payload available</span>
                )}
              </div>
            </details>

            <details className="group [&_summary::-webkit-details-marker]:hidden" open>
              <summary className="flex cursor-pointer items-center justify-between rounded-md bg-neutral-900 px-4 py-2 font-medium text-neutral-300 hover:bg-neutral-800">
                Logs
                <span className="transition group-open:rotate-180">▼</span>
              </summary>
              <div className="mt-2 rounded-md bg-black p-4">
                {logs || job.error ? (
                  <pre className="max-h-96 overflow-auto font-mono text-xs text-neutral-400 whitespace-pre-wrap">
                    {job.error && <span className="block mb-4 text-red-400">[ERROR] {job.error}</span>}
                    {logs}
                  </pre>
                ) : (
                  <span className="text-sm text-neutral-500">No logs available</span>
                )}
              </div>
            </details>

            {/* Pi Session panel — structured execution metadata from the pi
                CLI runner (PIO-01). Renders only when piSession is present;
                older jobs without it just skip this section. */}
            {piSession && <PiSessionPanel session={piSession} />}

            <details className="group [&_summary::-webkit-details-marker]:hidden">
              <summary className="flex cursor-pointer items-center justify-between rounded-md bg-neutral-900 px-4 py-2 font-medium text-neutral-300 hover:bg-neutral-800">
                Code (Replay)
                <span className="transition group-open:rotate-180">▼</span>
              </summary>
              <div className="mt-2 rounded-md bg-black p-4 relative group/code">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="absolute top-2 right-2 opacity-0 group-hover/code:opacity-100 transition-opacity bg-neutral-800 hover:bg-neutral-700"
                  onClick={() => {
                    if (typeof window !== 'undefined' && window.navigator?.clipboard) {
                      const payloadStr = job.inputPayload ? `'${JSON.stringify(job.inputPayload)}'` : "''";
                      window.navigator.clipboard.writeText(`ceo agent invoke ${job.agentId} --trigger ${job.trigger} --payload ${payloadStr}`);
                    }
                  }}
                  title="Copy to clipboard"
                >
                  <span className="text-xs">📋</span>
                </Button>
                <pre className="overflow-auto font-mono text-xs text-neutral-400">
                  <span className="text-blue-400">ceo</span> agent invoke {job.agentId} \
                  <br/>  --trigger {job.trigger} \
                  {job.inputPayload != null && (
                    <><br/>  --payload '{JSON.stringify(job.inputPayload as object)}'</>
                  )}
                </pre>
              </div>
            </details>

            {/* Code Changes Section - Git diff output */}
            {typeof cleanOutputPayload?.codeSnapshot === 'string' && (
              <details className="group [&_summary::-webkit-details-marker]:hidden">
                <summary className="flex cursor-pointer items-center justify-between rounded-md bg-neutral-900 px-4 py-2 font-medium text-neutral-300 hover:bg-neutral-800">
                  Code Changes
                  <span className="transition group-open:rotate-180">▼</span>
                </summary>
                <div className="mt-2 rounded-md bg-black p-4">
                  <pre className="overflow-auto font-mono text-xs text-neutral-400 whitespace-pre-wrap max-h-48">
                    {cleanOutputPayload.codeSnapshot}
                  </pre>
                </div>
              </details>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

